import React, { useState, useMemo, useCallback } from 'react';
import { CardsWithIcon } from '../../../../component/cards/cards-with-icon';
import { Chart } from 'primereact/chart';
import globalUserPage from '../../../../stores/global-user-page';
import { UserPaymentServiceWithPriceList } from '../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { UserSummaryType } from '../../../../api/types/user/user-summary.type';
import { PelecardResponse } from '../i-fram-asd';
import { getPaymentLinkOperations } from '../payment-services/operations/get-payment-link-operations';
import { PaymentResponse } from '../../../../api/types/payments/gateway/payment-gateway-response';
import { onPaymentSuccessOperation } from '../payment-services/operations/on-payment-success-operation';
import { PaymentIframeDialog } from './components/payment-iframe-dialog';
import {
  PaymentMethodSelectionDialog,
  PaymentMethod,
  AllocationWithAmount,
} from '../payment-methods/payment-method-selection-dialog';
import {
  UserPagePaymentsTable,
  EnrichedUserPaymentService,
} from './tabs/payment-services/user-page-payments-table';
import TabBar from '../../../../component/tab-bar/tab-bar';
import { UserPageStudentPaymentItems } from '../student/user-page-student-payment-items';
import { PaymentSummarySection } from './components/payment-summary-section';

const tabs = ['שירותי תשלום', 'תשלומים לא מקושרים'];
interface UserPagePaymentsDataModel {
  user: UserSummaryType;
}

export const UserPagePayments: React.FC<UserPagePaymentsDataModel> = ({
  user,
}) => {
  const [paymentUrl, setPaymentUrl] = useState<
    (PaymentResponse & { allocationId: number }) | null
  >(null);
  const [loadingNotifySuccess, setLoadingNotifySuccess] = useState(false);
  const [showPaymentMethodDialog, setShowPaymentMethodDialog] = useState(false);
  const [allocationsForPayment, setAllocationsForPayment] = useState<
    AllocationWithAmount[]
  >([]);
  const [selectedRows, setSelectedRows] = useState<
    EnrichedUserPaymentService[]
  >([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const getEffectivePrice = useCallback(
    (service: UserPaymentServiceWithPriceList): number => {
      const allocation = service.allocations?.allocationEntity;
      const override =
        (allocation?.overrideSum as number | null | undefined) ??
        (allocation?.overridePrice as number | null | undefined) ??
        null;
      const basePrice = parseFloat(String(service.currentPrices?.price || 0));
      return override !== null && !Number.isNaN(override)
        ? override
        : basePrice;
    },
    [],
  );

  const handlePaymentMethodSelected = async (
    method: PaymentMethod,
    allocations: AllocationWithAmount[],
    additionalData?: any,
  ) => {
    switch (method) {
      case PaymentMethod.CreditCard:
        // For credit card, if multiple allocations, we need to handle differently
        // For now, handle only single allocation for credit card
        if (allocations.length === 1) {
          await handleGenerateLink(
            allocations[0].allocationId,
            allocations[0].amount,
          );
        } else {
          console.error(
            'Credit card payment not supported for multiple allocations yet',
          );
        }
        break;

      case PaymentMethod.BankTransfer:
      case PaymentMethod.Check:
      case PaymentMethod.Scholarship:
      case PaymentMethod.Cash:
        // These are handled in the dialog via API call
        // Just close dialog and clear selection
        setShowPaymentMethodDialog(false);
        setAllocationsForPayment([]);
        setSelectedRows([]);
        break;
    }
  };

  const handleOnPayment = async (
    allocationId: number,
    paymentServiceName: string,
    amount: number,
  ) => {
    setAllocationsForPayment([
      {
        allocationId,
        paymentServiceName,
        maxAmount: amount,
        amount: amount,
      },
    ]);
    setShowPaymentMethodDialog(true);
  };

  const handlePaySelectedRows = () => {
    if (selectedRows.length === 0) return;

    const allocations = selectedRows
      .filter((row) => {
        const totalPaid =
          row.userPayments?.reduce(
            (sum, payment) => sum + payment.itemAmount,
            0,
          ) ?? 0;
        const remainingAmount = getEffectivePrice(row) - totalPaid;
        return remainingAmount > 0;
      })
      .map((row) => {
        const totalPaid =
          row.userPayments?.reduce(
            (sum, payment) => sum + payment.itemAmount,
            0,
          ) ?? 0;
        const remainingAmount = getEffectivePrice(row) - totalPaid;

        return {
          allocationId: row.allocations.allocationEntity.id,
          paymentServiceName: row.paymentService.paymentServiceName,
          maxAmount: remainingAmount,
          amount: remainingAmount,
        };
      });

    if (allocations.length === 0) {
      console.log('No allocations with outstanding amounts');
      return;
    }

    setAllocationsForPayment(allocations);
    setShowPaymentMethodDialog(true);
  };

  const handleGenerateLink = async (allocationId: number, amount: number) => {
    await getPaymentLinkOperations(
      allocationId,
      user.id,
      amount,
      setPaymentUrl,
    );
  };

  const handleOnSuccessPayment = async (data: PelecardResponse) => {
    await onPaymentSuccessOperation(
      paymentUrl?.allocationId,
      user.id,
      paymentUrl?.pendingPaymentId,
      setLoadingNotifySuccess,
      setPaymentUrl,
    );
  };

  const resetLink = () => setPaymentUrl(null);

  const totalAllocation =
    globalUserPage?.studentDetails?.paymentServiceDetails ?? [];

  const fullyPaidCount =
    globalUserPage?.studentDetails?.paymentServiceDetails?.reduce(
      (count, service) => {
        const totalPayments =
          service.userPayments?.reduce(
            (sum, payment) => sum + parseFloat(String(payment.itemAmount || 0)),
            0,
          ) ?? 0;
        const requiredPrice = getEffectivePrice(service);
        return count + (totalPayments >= requiredPrice ? 1 : 0);
      },
      0,
    ) ?? 0;

  const totalOverallPayments =
    globalUserPage?.studentDetails?.paymentServiceDetails?.reduce(
      (total, service) => {
        const servicePayments = service.userPayments
          ? service.userPayments.reduce(
              (sum, payment) =>
                sum + parseFloat(String(payment.itemAmount || 0)),
              0,
            )
          : 0;
        return total + servicePayments;
      },
      0,
    ) ?? 0;

  const totalCurrentPrices =
    globalUserPage?.studentDetails?.paymentServiceDetails?.reduce(
      (total, service) => {
        return total + getEffectivePrice(service);
      },
      0,
    ) ?? 0;

  const totalPaymentItemsAmount =
    globalUserPage?.studentDetails?.paymentItems?.reduce(
      (total, item) => total + parseFloat(String(item.amount || 0)),
      0,
    ) ?? 0;

  const totalIncome = totalOverallPayments + totalPaymentItemsAmount;

  const uniqueCount = new Set(
    totalAllocation.flatMap((alloc) => alloc.userPayments.map((p) => p.id)),
  ).size;

  // Summary calculations
  const linkedPaymentsCount = uniqueCount;
  const linkedPaymentsTotal = totalOverallPayments;
  const unlinkedPaymentsCount =
    globalUserPage?.studentDetails?.paymentItems?.length ?? 0;
  const unlinkedPaymentsTotal = totalPaymentItemsAmount;

  const chartData = useMemo(() => {
    const documentStyle = getComputedStyle(document.documentElement);
    const blueColor = documentStyle.getPropertyValue('--blue-500') || '#3B82F6';
    const greenColor =
      documentStyle.getPropertyValue('--green-500') || '#22C55E';
    const blueHover = documentStyle.getPropertyValue('--blue-400') || '#60A5FA';
    const greenHover =
      documentStyle.getPropertyValue('--green-400') || '#4ADE80';

    const debtValue = Math.max(0, totalCurrentPrices - totalIncome);
    const incomeValue = totalIncome;

    const data = {
      labels: ['חובות', 'הכנסות'],
      datasets: [
        {
          data: [debtValue, incomeValue],
          backgroundColor: [blueColor, greenColor],
          hoverBackgroundColor: [blueHover, greenHover],
        },
      ],
    };

    return data;
  }, [totalCurrentPrices, totalIncome]);

  const chartOptions = useMemo(
    () => ({
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            usePointStyle: true,
          },
        },
      },
    }),
    [],
  );

  type PaymentStatus = 'שולם' | 'שולם חלקית' | 'לא שולם';

  const calculatePaymentStatus = (
    service: UserPaymentServiceWithPriceList,
  ): PaymentStatus => {
    const totalPaymentAmount =
      service.userPayments?.reduce(
        (sum, payment) => sum + parseFloat(String(payment.itemAmount || 0)),
        0,
      ) ?? 0;
    const currentPriceToPay = getEffectivePrice(service);

    if (totalPaymentAmount >= currentPriceToPay && currentPriceToPay > 0) {
      return 'שולם';
    } else if (totalPaymentAmount > 0) {
      return 'שולם חלקית';
    }
    return 'לא שולם';
  };

  // Create a function to calculate total payment (separated for reusability and cleaner code)
  const calculateTotalPayment = (
    service: UserPaymentServiceWithPriceList,
  ): number => {
    return (
      service.userPayments?.reduce(
        (sum, payment) => sum + parseFloat(String(payment.itemAmount || 0)),
        0,
      ) ?? 0
    );
  };

  // Get all installment plans for passing to PaymentServiceHistoryList
  const creditCardInstallmentPlans =
    globalUserPage.studentDetails?.installments ?? [];
  const bankTransferInstallmentPlans =
    globalUserPage.studentDetails?.installmentsBankTransfer ?? [];

  // Create a function to calculate installment amount for a specific allocation
  const calculateInstallmentAmountForAllocation = useCallback(
    (allocationId: number): number => {
      let totalInstallmentAmount = 0;

      // Check credit card installment plans
      creditCardInstallmentPlans.forEach((plan) => {
        const allocationItem = plan.paymentInstallmentPlanItemAllocations.find(
          (item) => item.userAllocationId === allocationId,
        );
        if (allocationItem) {
          totalInstallmentAmount += allocationItem.allocationAmount;
        }
      });

      // Check bank transfer installment plans
      bankTransferInstallmentPlans.forEach((plan) => {
        const allocationItem = plan.paymentInstallmentPlanItemAllocations.find(
          (item) => item.userAllocationId === allocationId,
        );
        if (allocationItem) {
          totalInstallmentAmount += allocationItem.allocationAmount;
        }
      });

      return totalInstallmentAmount;
    },
    [creditCardInstallmentPlans, bankTransferInstallmentPlans],
  );

  // Create the enriched data array
  const enrichedPaymentServices: EnrichedUserPaymentService[] = useMemo(
    () =>
      globalUserPage.studentDetails?.paymentServiceDetails?.map((service) => ({
        ...service,
        totalPaymentAmount: calculateTotalPayment(service),
        paymentStatus: calculatePaymentStatus(service),
        installmentAmount: calculateInstallmentAmountForAllocation(
          service.allocations.allocationEntity.id,
        ),
      })) ?? [],
    [
      globalUserPage.studentDetails?.paymentServiceDetails,
      calculateInstallmentAmountForAllocation,
    ],
  );

  return (
    <div style={{ width: '100%' }}>
      <PaymentMethodSelectionDialog
        visible={showPaymentMethodDialog}
        onHide={() => {
          setShowPaymentMethodDialog(false);
          setAllocationsForPayment([]);
          setSelectedRows([]);
        }}
        allocations={allocationsForPayment}
        user={user}
        onPaymentMethodSelected={handlePaymentMethodSelected}
      />
      <PaymentIframeDialog
        paymentUrl={paymentUrl}
        loadingNotifySuccess={loadingNotifySuccess}
        onHide={resetLink}
        onPaymentSuccess={handleOnSuccessPayment}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          /*flexWrap: 'wrap', // Allows items to wrap to the next row*/
          gap: '16px', // Adds space between items
          height: 'auto',
        }}
      >
        <CardsWithIcon
          title={'כמות שרותים'}
          icon="pi pi-shopping-cart"
          iconBgColor={'#D7EBFF'}
          bodyText={[
            `${fullyPaidCount} מתוך ${totalAllocation.length} נרכש`,
            `${totalIncome.toFixed(2)}₪ נכנס מתוך ${totalCurrentPrices}₪`,
            <span
              key="balance"
              style={{
                fontWeight: 700,
                fontSize: '17px',
              }}
            >
              {'יתרה לתשלום: '}
              <span
                style={{
                  color: '#b91c1c',
                  direction: 'ltr',
                  display: 'inline-block',
                }}
              >
                {`${(totalCurrentPrices - totalIncome).toFixed(2)}₪`}
              </span>
            </span>,
          ]}
        />
        {(() => {
          console.log('Rendering Chart with data:', chartData);
          return null;
        })()}
        <Chart
          key={`pie-chart-${totalCurrentPrices}-${totalIncome}`}
          style={{ height: '200px', width: '200px' }}
          type="pie"
          data={chartData}
          options={chartOptions}
        />
        <PaymentSummarySection
          unlinkedPaymentsCount={unlinkedPaymentsCount}
          unlinkedPaymentsTotal={unlinkedPaymentsTotal}
          linkedPaymentsCount={linkedPaymentsCount}
          linkedPaymentsTotal={linkedPaymentsTotal}
        />
      </div>

      <TabBar
        tabs={tabs}
        onClick={(index) => {
          setSelectedTabIndex(index);
        }}
      />

      <div style={{ marginTop: '20px' }}>
        <div style={{ display: selectedTabIndex === 0 ? 'block' : 'none' }}>
          <UserPagePaymentsTable
            user={user}
            enrichedPaymentServices={enrichedPaymentServices}
            creditCardInstallmentPlans={creditCardInstallmentPlans}
            bankTransferInstallmentPlans={bankTransferInstallmentPlans}
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            onPayment={handleOnPayment}
            onPaySelectedRows={handlePaySelectedRows}
          />
        </div>
        <div style={{ display: selectedTabIndex === 1 ? 'block' : 'none' }}>
          <UserPageStudentPaymentItems />
        </div>
      </div>
    </div>
  );
};
